import { createAsyncThunk } from '@reduxjs/toolkit'
import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import productAdminServices from '../../../data/services/productAdminServices'
import { composeMeasurement } from '../../adpaters/measurementProduct/composeMeasurement'
import { createFilesArray } from '../../adpaters/product/createFiles'
import normalizeProduct from '../../adpaters/product/normalizeProduct'
import { createVariant } from '../../adpaters/variants/createVariant'
import fetchProductDetail from '../productDetail/fetchProductDetail'

const filterMedia = media => mediaIds =>
  media.filter(({ preview, src }) =>
    mediaIds.some(mediaId =>
      mediaId === preview?.image?.url || mediaId === src
    )
  )

const transformMedia = media => mediaIds =>
  filterMedia(media)(mediaIds).map(media => ({
    id: media.id,
    url: media.preview?.image?.url || media.src,
  }))

// Función para limpiar objetos de media eliminando src (base64 largo)
const cleanMediaForVariant = mediaArray =>
  mediaArray.map(media => {
    // eslint-disable-next-line no-unused-vars
    const { src, ...cleanMedia } = media
    return cleanMedia
  })

const fetchProductUpdate = createAsyncThunk(
  'product/update',
  async (
    { data, status, allFiles, passableEdited },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const {
        manual,
        certificate,
        efficiency,
        dataSheet,
        inpage,
        descriptionImage,
        media = null,
        seller,
      } = data
      const form = new FormData()
      const files = createFilesArray(
        manual,
        certificate,
        efficiency,
        dataSheet,
        inpage,
        descriptionImage
      )

      const filesIds = {}
      if (!isEmpty(files)) {
        files?.forEach(file => {
          form.append(file.metafield, file.file)
        })
        const response = await productAdminServices.uploadFiles(form)

        response?.forEach(file => {
          if (file.image) {
            filesIds[file.alt] = file.image.url
          } else {
            filesIds[file.alt] = file.url
          }
        })
      }

      const mediaIds = []
      let formMedia = null
      let mediaResponse = null
      const srcToUrlMap = {} // Mapa para traducir src local → URL del servidor

      // Primero, mapear todas las imágenes existentes que ya tienen URL del servidor
      if (!isEmpty(media)) {
        media.forEach(image => {
          // Si la imagen ya tiene URL del servidor, mapearla
          if (image.preview?.image?.url) {
            // Mapear src → URL del servidor
            if (image.src) {
              srcToUrlMap[image.src] = image.preview.image.url
            }
            // También mapear URL → URL (para imágenes que ya estaban)
            srcToUrlMap[image.preview.image.url] = image.preview.image.url
          }
        })

        formMedia = new FormData()
        let hasNewFiles = false

        media?.forEach(image => {
          if (image.file) {
            const { name, file } = image
            formMedia.append(name, file)
            hasNewFiles = true
          }
        })

        // Solo subir si hay archivos nuevos
        if (hasNewFiles) {
          mediaResponse = await productAdminServices.uploadFiles(formMedia)

          if (!isEmpty(mediaResponse)) {
            mediaResponse?.forEach((file) => {
              const originalMedia = media.find(m => m.name === file.alt || m.file?.name === file.alt)

              if (file.image) {
                mediaIds.push({ name: file.alt, url: file.image.url })
                // Actualizar mapeo con la URL del servidor para imágenes nuevas
                if (originalMedia?.src) {
                  srcToUrlMap[originalMedia.src] = file.image.url

                  // Actualizar el objeto media con la estructura completa del servidor
                  originalMedia.id = file.id
                  originalMedia.alt = file.alt
                  originalMedia.createdAt = file.createdAt
                  originalMedia.mimeType = file.mimeType
                  originalMedia.status = file.status
                  originalMedia.mediaContentType = file.mediaContentType
                  originalMedia.__typename = file.__typename

                  // Actualizar preview con estructura completa
                  originalMedia.preview = {
                    status: file.preview?.status,
                    image: {
                      src: file.preview?.image?.src,
                      url: file.preview?.image?.url,
                      altText: file.preview?.image?.altText,
                      id: file.preview?.image?.id,
                      width: file.preview?.image?.width,
                      height: file.preview?.image?.height
                    }
                  }
                }
              } else {
                mediaIds.push({ name: file.alt, url: file.url })
                // Actualizar mapeo con la URL del servidor para imágenes nuevas
                if (originalMedia?.src) {
                  srcToUrlMap[originalMedia.src] = file.url

                  // Actualizar el objeto media con la estructura completa
                  originalMedia.id = file.id
                  originalMedia.alt = file.alt
                  if (file.createdAt) originalMedia.createdAt = file.createdAt
                  if (file.mimeType) originalMedia.mimeType = file.mimeType
                  if (file.status) originalMedia.status = file.status
                  if (file.mediaContentType) originalMedia.mediaContentType = file.mediaContentType
                  if (file.__typename) originalMedia.__typename = file.__typename

                  // Actualizar preview
                  if (file.preview) {
                    originalMedia.preview = {
                      status: file.preview?.status,
                      image: {
                        src: file.preview?.image?.src || file.url,
                        url: file.preview?.image?.url || file.url,
                        altText: file.preview?.image?.altText || file.alt,
                        id: file.preview?.image?.id || file.id
                      }
                    }
                  } else {
                    originalMedia.preview = {
                      image: {
                        url: file.url,
                        id: file.id
                      }
                    }
                  }
                }
              }
            })
          }
        }
      }

      const variantList = data?.variants?.map(variant => {
        // Traducir mediaIds de src local a URL del servidor
        const translatedMediaIds = variant?.mediaIds?.map(id =>
          srcToUrlMap[id] || id
        )

        return createVariant({
          variantId: variant?.variantId,
          own_sku:
            isNil(variant?.ownSku) || variant?.ownSku === 'undefined'
              ? ''
              : variant?.ownSku,
          sku:
            isNil(variant?.sku) || variant?.sku === 'undefined'
              ? ''
              : variant?.sku,
          price: variant?.price || variant.compareAtPrice,
          compareAtPrice: variant?.compareAtPrice,
          color: variant?.color,
          weight: variant?.weight,
          availableQuantity: variant?.inventoryQuantity,
          imageName: translatedMediaIds
            ? transformMedia(media)(translatedMediaIds)
            : null,
          mediaForVariant:
            translatedMediaIds?.length > 0
              ? cleanMediaForVariant(filterMedia(media)(translatedMediaIds))
              : null,
          currentMedia: translatedMediaIds
            ? transformMedia(media)(translatedMediaIds)[0]
            : null,
          inventoryLevelId: variant?.inventoryItem?.inventoryLevel?.id,
          currentAvailable:
            variant?.inventoryQuantity ??
            variant?.inventoryItem?.inventoryLevel?.quantities?.find(q => q.name === 'available')?.quantity ??
            variant?.inventoryItem?.inventoryLevel?.available ??
            0,
          inventoryItemId: variant?.inventoryItem?.id,
          locationId: variant?.locationId,
          availableDelta:
            variant?.inventoryQuantity - variant?.currentAvailable
        })
      })

      let newStatus = status
      if (status === 'ACTIVE' && passableEdited) {
        newStatus = 'DRAFT'
      }

      const currentFiles = allFiles
        ? Object.keys(allFiles).reduce((acc, key) => {
          const metafieldKey = key
          if (allFiles[key]?.value) {
            acc[metafieldKey] = allFiles[key]?.value
          } else if (filesIds[key]) {
            acc[metafieldKey] = filesIds[key]
          }
          return acc
        }, {})
        : {}

      const { productUpdate } = await productAdminServices.updateProduct({
        id: data.id,
        title: data.productName,
        productType: data.category,
        status: newStatus,
        vendor: data.brand,
        tags: data.tags,
        descriptionHtml: data.detailedDescription,
        availableQuantity: data?.availableQuantity,
        warranty: data?.productWarranty,
        materials: data?.productMaterial,
        features: data?.specialFeatures,
        fabricado_en: data?.manufacturedCountry,
        tension: data?.productTension,
        installation: data?.productInstalation,
        accesories: data?.productAccesories,
        considerations: data?.productConsiderations,
        plug: data?.plug,
        knob: data?.commands,
        power: data?.power,
        rating_energy: data?.energyClass,
        cp_useful_total: data?.totalUsableCapacity,
        measurements_enchase: data?.embeddedMeasurements,
        measurements_product: composeMeasurement(
          data.productHeight,
          data.productWidth,
          data.productDepth,
          data.productVolume
        ),
        panel: data?.panel,
        type_power: data?.energyType,
        speed: data?.speedsNumber,
        light: data?.light,
        armed: data?.built,
        consumption_kwh: data?.consume,
        temperature: data?.temperature,
        level_heat_burner: data?.burnersHeatLevels,
        type_surface: data?.countertopType,
        total_burners: data?.burnersNumber,
        type_oven: data?.ovenType,
        total_programs: data?.ovenProgramsNumber,
        max_suction: data?.maxSuction,
        type_campaign: data?.bellType,
        max_noise: data?.maxNoise,
        type_grill: data?.grillType,
        diameter_tube: data?.tubeDiameter,
        technology_cooling: data?.coldTechnology,
        type_freezer: data?.freezerType,
        type_refrigerator: data?.refrigeratorType,
        cp_useful_freezer: data?.freezerUsableCapacity,
        cp_useful_refrigerator: data?.refrigeratorUsableCapacity,
        gas_refrigerant: data?.refrigerantGas,
        total_programs_washing: data?.washingProgramsNumber,
        total_programs_drying: data?.dryProgramsNumber,
        type_load: data?.loadType,
        video: data.video,
        ...filesIds,
        ...currentFiles,
        media: mediaIds,
        variants: variantList,
        mediaToDelete: data.mediaToDelete,
        mediaPosition: data.mediaPosition || [],
        intensity: data.intensity,
        security: data.security,
        cable_length: data.cableLength,
        type_tap: data.tapType,
        cp_useful_washing_machine: data.washingMachineUsableCapacity,
        cp_useful_washer_dryer: data.dryerUsableCapacity,
        type_function: data.functionType,
        bottles_quantity: data.bottlesQuantity,
        seller
      })
      if (isEmpty(productUpdate?.product) || !productUpdate) {
        return rejectWithValue('Error actualizar producto')
      }
      const product = normalizeProduct(productUpdate?.product)
      dispatch(fetchProductDetail({ id: product?.id }))
      return product
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export default fetchProductUpdate
